// modules
import { CommonOrderValidity, CommonOrderSide, OrderCategory, OrderStatuses } from 'modules/Ordering';
import OrdersTVService from 'modules/TradesPW3/Orders';
import Format from 'modules/Format';
// typings
import { PreRiskData } from 'modules/PreRisk/typings';
import { PositionBase, SurveillanceShownMap } from './typings';

// Filter out positions for exit all
export const getNonDeliveryPositions = (positions: PositionBase[]) =>
  positions.filter(position => {
    const { product, instrument } = position;

    // Remove delivery positions only for NSE_EQ or BSE_EQ
    if (product === 'D' && (instrument.e === 'NSE_EQ' || instrument.e === 'BSE_EQ')) {
      return false;
    }

    // Remove MTF and MTFS for all cases
    if (product === 'MTF' || product === 'MTFS') {
      return false;
    }

    return true;
  });

// Helper function to extract instrument keys
export const extractOpenPositionInstruments = (
  positionsToExit: PositionBase[]) => positionsToExit.reduce<string[]>(
    (acc, position) => {
      acc.push(position.instrument_key);
      return acc;
    }, []);

export const generateSquareOffData = (
  position: PositionBase,
  surveillance: boolean,
  limitPrice?: number,
): CommonOrder => {
  const quantity = Math.abs(position.netInfo.buyQty - position.netInfo.sellQty);
  const side = position.netInfo.buyQty > position.netInfo.sellQty ? 'S' : 'B';
  return {
    orderCategory: OrderCategory.REGULAR,
    additionalInfo: {
      surveillance,
      itemId: Format.generateUUID(),
    },
    ...(limitPrice && { price: limitPrice }),
    amo: false,
    disclosedQuantity: 0,
    instrumentKey: position.instrument_key,
    orderType: limitPrice ? 'L' : 'M',
    productType: position.product,
    quantity,
    side: side === 'B' ? CommonOrderSide.B : CommonOrderSide.S,
    validity: CommonOrderValidity.D,
  };
};

export const getPositionSecondLeg = (
  orders: Order[],
  instrumentKey: string,
) => {
  const filter = (order: Order): boolean => {
    const orderInstrumentKey = order.instrument_key;
    const productType = order.product;
    const orderStatus = OrdersTVService.mapStatus(order.status, order.isAMO);
    const orderIsOpen =
        orderStatus === OrderStatuses.O || orderStatus === OrderStatuses.TP;

    return (
      orderInstrumentKey === instrumentKey &&
      (productType === 'CO' || productType === 'OCO') &&
      order.orderNumber.parent !== 'NA' &&
      orderIsOpen
    );
  };

  const secondLegsOrderIds = orders
    .filter(order => filter(order))
    .map(({ orderNumber }) => orderNumber.oms);

  return secondLegsOrderIds;
};

export const prepareExitOrdersRequestData = (
  positionsToExit: PositionBase[],
  isSurveillanceShownMap: SurveillanceShownMap,
) => {
  const placeOrderRequestList: CommonOrder[] = [];
  const cancelOrderRequestList: string[] = [];
  positionsToExit.forEach((position) => {
    const { product } = position;
    if (product === 'CO' || product === 'OCO') {
      const secondLegsOrderIds = getPositionSecondLeg(
        OrdersTVService.orders,
        position.instrument_key,
      );
      cancelOrderRequestList.push(...secondLegsOrderIds);
    } else {
      const orderRequestData: CommonOrder = generateSquareOffData(
        position,
        !!isSurveillanceShownMap[position.instrument_key],
      );
      placeOrderRequestList.push(orderRequestData);
    }
  });
  return {
    placeOrderRequestList,
    cancelOrderRequestList,
  };
};

export const getSurveillanceShownMap = (surveillanceData: PreRiskData[]) =>
  surveillanceData?.reduce(
    (acc, item) => {
      acc[item.instrumentKey] = true;
      return acc;
    },
    {} as { [key: string]: boolean },
  );
